import { type ReactNode } from 'react';
import { colors } from '../styles/theme';
import { useMovablePopup } from './MovablePopup';
import { useModalFocus } from '../hooks/useModalFocus';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Maximum width — defaults to a comfortable form-sized dialog. */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl';
  /** Disable click-outside / ESC dismissal. Useful while a request is
   *  in flight so the user can't accidentally close the dialog
   *  half-way through a destructive operation. */
  busy?: boolean;
  children: ReactNode;
}

const MAX_WIDTH_PX: Record<NonNullable<Props['maxWidth']>, string> = {
  sm: '24rem',
  md: '28rem',
  lg: '32rem',
  xl: '36rem',
};

/**
 * Shell for app-wide modal dialogs.
 *
 * Owns the overlay, click-outside dismissal, ESC handling and
 * `stopPropagation`. Bodies lay out their own form/content. Eight ad
 * hoc reimplementations of this used to live across the codebase —
 * keep this one as the single source of truth so a UX change (e.g.
 * focus-trap, reduced-motion overlay) is a one-line edit.
 *
 * It is modal for the keyboard and for a screen reader too (`useModalFocus`): focus
 * moves in when it opens, stays inside, and returns to the opener when it closes; the
 * body's first heading names it.
 *
 * It opens centred and the person can move it (see `useMovablePopup`).
 */
const Modal = ({
  open,
  onClose,
  maxWidth = 'lg',
  busy = false,
  children,
}: Props): JSX.Element | null => {
  const popup = useMovablePopup<HTMLDialogElement>();
  useModalFocus(popup.panelRef, open, busy ? undefined : onClose);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={busy ? undefined : onClose}
        className="absolute inset-0 w-full h-full cursor-default"
        style={{ background: 'rgba(0,0,0,0.45)', border: 'none' }}
      />
      <dialog
        ref={popup.panelRef}
        open
        aria-modal="true"
        tabIndex={-1}
        className="relative rounded-2xl p-6 w-full"
        style={{
          ...popup.panelStyle,
          background: colors.surface,
          zIndex: 1,
          margin: 0,
          border: 'none',
          maxWidth: MAX_WIDTH_PX[maxWidth],
          maxHeight: 'none',
          color: 'inherit',
          outline: 'none',
        }}
        onCancel={busy ? undefined : onClose}
      >
        {popup.bar}
        {children}
      </dialog>
    </div>
  );
};

export default Modal;
